import { useState } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, List, Trash2, Share2, AlertCircle } from 'lucide-react'
import { getPersonalLists, createList, deleteList, getListItems } from '../../services/api'
import { useTimedOut } from '../../hooks/useTimedOut'
import { formatRelativeTime } from '../../utils/projectDisplay'
import type { PersonalList } from '../../types'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import CreateListDialog from '../../components/lists/CreateListDialog'
import ListItems from '../../components/lists/ListItems'
import ShareListModal from '../../components/lists/ShareListModal'
import ConfirmDialog from '../../components/ui/ConfirmDialog'

export default function MyLists() {
  const queryClient = useQueryClient()
  const [selectedList, setSelectedList] = useState<PersonalList | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showShare, setShowShare] = useState<PersonalList | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PersonalList | null>(null)

  const { data: lists, isLoading, isError, error, refetch } = useQuery({ queryKey: ['lists'], queryFn: getPersonalLists })
  // BUG-VIS-4-4: the master-detail rail used to be gated behind a bare
  // `if (isLoading) return <LoadingSkeleton />` that blocked the ENTIRE page
  // (including the header) for as long as the query stayed pending, with no
  // way out if it ever stalled. Bound it: once loading has run past a
  // reasonable wait, fall back to an honest retry state instead of an
  // infinite skeleton.
  const listsTimedOut = useTimedOut(isLoading, 8000)

  const { data: items } = useQuery({
    queryKey: ['list-items', selectedList?.list_id],
    queryFn: () => getListItems(selectedList!.list_id),
    enabled: !!selectedList,
  })

  // Real, live item-count badge per list tile (Fig46) — list_lists (the
  // getPersonalLists response) has no aggregate item count, so it's derived
  // client-side from the same getListItems() call the detail panel already
  // uses (same query key → shared cache, no duplicate fetch on selection).
  const itemCountQueries = useQueries({
    queries: (lists ?? []).map((l) => ({
      queryKey: ['list-items', l.list_id],
      queryFn: () => getListItems(l.list_id),
      staleTime: 60 * 1000,
    })),
  })
  const itemCountByListId: Record<string, number> = {}
  ;(lists ?? []).forEach((l, i) => {
    const count = itemCountQueries[i]?.data?.length
    if (count !== undefined) itemCountByListId[l.list_id] = count
  })

  // BUG-126: CreateListDialog keeps itself open and shows the real server error
  // when this throws, so there is no silent failure.
  const createMutation = useMutation({
    mutationFn: (v: { name: string; description: string }) => createList(v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      queryClient.invalidateQueries({ queryKey: ['personal-lists'] })
      setShowCreate(false)
    },
  })

  // BUG-126 / P7: the delete confirmation runs the real mutation inside
  // ConfirmDialog.onConfirm, which awaits it and keeps the dialog open with the
  // true error if it throws. No fake success — onSuccess only fires on a genuine
  // server confirmation.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      if (selectedList?.list_id === deleteTarget?.list_id) setSelectedList(null)
    },
  })

  return (
    <div>
      <PageHeader
        title="My Lists"
        description="Your personal collections of patents, journals and discoveries. Select a list to view its items, then attach one to a project when it becomes relevant."
        helpKey="lists"
        primaryAction={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
          >
            <Plus size={16} /> New List
          </button>
        }
      />

      {isLoading && !listsTimedOut ? (
        <div data-testid="lists-loading-skeleton">
          <LoadingSkeleton rows={4} />
        </div>
      ) : (isError || listsTimedOut) && !lists?.length ? (
        <EmptyState
          icon={AlertCircle}
          title={isError ? 'Failed to load lists' : 'Lists are taking longer than expected'}
          description={
            isError
              ? (error as Error)?.message || 'An unexpected error occurred'
              : 'The list service did not respond in time.'
          }
          action={
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
            >
              Retry
            </button>
          }
        />
      ) : !lists?.length ? (
        <EmptyState
          icon={List}
          title="No lists yet"
          description="Create a list to organise and save your favourite patents, journals and discoveries, then attach them to a project when they become relevant."
          action={
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
            >
              <Plus size={16} /> Create List
            </button>
          }
        />
      ) : (
        // Master-detail (Fig46): a left rail of every list + a right panel showing
        // the selected list's items, so switching lists never navigates away.
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="w-full lg:w-72 flex-shrink-0 light-card p-3">
            <h2 className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Your Lists</h2>
            <div className="space-y-1">
              {lists.map((list) => {
                const isSelected = selectedList?.list_id === list.list_id
                return (
                  <div
                    key={list.list_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedList(list)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedList(list) }}
                    aria-current={isSelected || undefined}
                    className={`group flex items-start justify-between gap-2 rounded-lg p-2.5 cursor-pointer transition-colors ${
                      isSelected ? 'bg-iflow-50 border border-iflow-300' : 'border border-transparent hover:bg-white/70'
                    }`}
                  >
                    <div className="min-w-0 flex items-start gap-2">
                      <List size={14} className={`mt-0.5 flex-shrink-0 ${isSelected ? 'text-iflow-600' : 'text-iflow-500'}`} />
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-iflow-700' : 'text-gray-900'}`}>{list.name}</p>
                        {/* Real relative timestamp (Fig46) — matches the "Updated 2h
                            ago" style used elsewhere in the app, instead of an
                            absolute date. */}
                        <p className="text-xs text-gray-500">Updated {formatRelativeTime(list.updated_at || list.created_at) || 'just now'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Real, live item-count badge (Fig46) — derived from the
                          actual list-items query, never fabricated; omitted while
                          the count hasn't loaded yet rather than showing a false 0. */}
                      {itemCountByListId[list.list_id] !== undefined && (
                        <span
                          title={`${itemCountByListId[list.list_id]} item${itemCountByListId[list.list_id] === 1 ? '' : 's'}`}
                          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            isSelected ? 'bg-iflow-100 text-iflow-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {itemCountByListId[list.list_id]}
                        </span>
                      )}
                      <div className="flex gap-1 lg:opacity-0 lg:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowShare(list) }}
                        aria-label={`Share ${list.name}`}
                        className="p-1 text-gray-500 hover:text-iflow-600"
                      >
                        <Share2 size={13} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(list) }}
                        aria-label={`Delete ${list.name}`}
                        className="p-1 text-gray-500 hover:text-red-600"
                      >
                        <Trash2 size={13} />
                      </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex-1 min-w-0 w-full">
            {selectedList ? (
              <div className="light-card p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedList.name}</h3>
                    {selectedList.description && <p className="text-sm text-gray-500 mt-0.5">{selectedList.description}</p>}
                  </div>
                  <button
                    onClick={() => setShowShare(selectedList)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 flex-shrink-0"
                  >
                    <Share2 size={14} /> Share
                  </button>
                </div>
                <ListItems items={items || []} listId={selectedList.list_id} />
              </div>
            ) : (
              <EmptyState
                icon={List}
                title="Select a list"
                description="Choose a list on the left to view and manage its saved items."
              />
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateListDialog
          onSubmit={async (name, description) => { await createMutation.mutateAsync({ name, description }) }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {showShare && <ShareListModal list={showShare} onClose={() => setShowShare(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete list"
        message={<>Delete <strong>&ldquo;{deleteTarget?.name}&rdquo;</strong>? This removes the list and everything saved in it. This cannot be undone.</>}
        confirmLabel="Delete"
        tone="danger"
        onConfirm={async () => {
          if (!deleteTarget) return
          try {
            await deleteMutation.mutateAsync(deleteTarget.list_id)
          } catch (err) {
            // Surface the real server error in the dialog; it stays open on failure.
            const e = err as { response?: { data?: { error?: string } }; message?: string }
            throw new Error(e?.response?.data?.error || e?.message || 'Failed to delete list')
          }
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}
